import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import TopBar from '../../components/TopBar';
import Splash from '../../components/Splash';
import {
  adminBanners,
  adminDeleteBanner,
  adminSaveBanner,
  adminSessions,
  adminUploadPhoto,
} from '../../api/endpoints';
import { showAlert, showConfirm, hapticNotify } from '../../lib/tg';
import { mediaUrl } from '../../lib/media';
import { formatDateTime } from '../../lib/format';

function normalizeUrl(raw: string): string {
  const v = raw.trim();
  if (!v) return v;
  // Telegram @username shorthand.
  if (/^@[A-Za-z0-9_]{3,}$/.test(v)) return `https://t.me/${v.slice(1)}`;
  if (/^t\.me\//i.test(v)) return `https://${v}`;
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

export default function Banners() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-banners'], queryFn: adminBanners });
  const { data: sessions } = useQuery({ queryKey: ['admin-sessions', false], queryFn: () => adminSessions(false) });

  const [imageFileId, setImageFileId] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [linkType, setLinkType] = useState<'none' | 'session' | 'url'>('none');
  const [linkSessionId, setLinkSessionId] = useState<string>('');
  const [linkUrl, setLinkUrl] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const addMut = useMutation({
    mutationFn: () =>
      adminSaveBanner({
        image_file_id: imageFileId,
        link_session_id: linkType === 'session' && linkSessionId ? Number(linkSessionId) : null,
        link_url: linkType === 'url' && linkUrl ? normalizeUrl(linkUrl) : null,
      } as any),
    onSuccess: () => {
      hapticNotify('success');
      setImageFileId(null);
      setImagePreview(null);
      setLinkSessionId('');
      setLinkUrl('');
      setLinkType('none');
      qc.invalidateQueries({ queryKey: ['admin-banners'] });
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail;
      showAlert(typeof detail === 'string' ? detail : 'Не удалось сохранить баннер');
    },
  });

  const delMut = useMutation({
    mutationFn: (id: number) => adminDeleteBanner(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-banners'] }),
  });

  async function onDelete(id: number) {
    if (await showConfirm('Удалить баннер?')) delMut.mutate(id);
  }

  if (isLoading) return <Splash />;

  const sessionById = new Map<number, any>();
  (sessions as any[])?.forEach((s) => sessionById.set(s.id, s));

  return (
    <div className="min-h-screen pb-8">
      <TopBar title="Баннеры" />
      <div className="px-4 pt-3 space-y-3">
        {data?.map((b) => {
          const sess = b.link_session_id ? sessionById.get(b.link_session_id) : null;
          return (
            <div key={b.id} className="card overflow-hidden">
              {b.image_url && <img src={mediaUrl(b.image_url)} className="w-full max-h-40 object-cover" />}
              <div className="p-3 flex items-center justify-between gap-3">
                <div className="text-xs text-muted min-w-0 flex-1 truncate">
                  {sess ? `→ ${sess.title} · ${formatDateTime(sess.starts_at)}`
                    : b.link_session_id ? `Сеанс #${b.link_session_id}`
                    : b.link_url || '—'}
                </div>
                <button className="text-danger text-sm shrink-0" onClick={() => onDelete(b.id)}>Удалить</button>
              </div>
            </div>
          );
        })}
        {!data?.length && <div className="text-muted text-center py-6">Пусто</div>}

        <div className="card p-3 space-y-3">
          <div className="font-semibold">Новый баннер</div>

          <div>
            <div className="label">Картинка</div>
            <div className="text-xs text-muted mb-2">
              Рекомендуется 16:8 (горизонтальный), ≥ 1200×600 px, JPG/PNG/WebP до 10 МБ.
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setUploading(true);
                setImagePreview(URL.createObjectURL(f));
                try {
                  const res = await adminUploadPhoto(f);
                  setImageFileId(res.file_id);
                } catch {
                  showAlert('Ошибка загрузки');
                  setImagePreview(null);
                } finally {
                  setUploading(false);
                  if (fileRef.current) fileRef.current.value = '';
                }
              }}
            />
            {imagePreview ? (
              <div className="flex items-center gap-3">
                <img src={imagePreview} className="w-28 h-16 object-cover rounded-lg2 border border-line" />
                <div className="flex flex-col gap-2">
                  <button className="btn-secondary text-sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
                    {uploading ? 'Загрузка…' : 'Заменить'}
                  </button>
                  <button
                    className="btn-secondary text-sm"
                    onClick={() => { setImageFileId(null); setImagePreview(null); }}
                  >
                    Удалить
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="btn-secondary w-full"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? 'Загрузка…' : 'Выбрать из галереи'}
              </button>
            )}
          </div>

          <div>
            <div className="label">Куда ведёт</div>
            <div className="flex gap-2 mb-2">
              {(['none', 'session', 'url'] as const).map((opt) => (
                <button
                  key={opt}
                  onClick={() => setLinkType(opt)}
                  className={`px-3 py-1.5 rounded-full text-xs ${linkType === opt ? 'bg-accent text-white' : 'bg-bg-elev text-muted'}`}
                >
                  {opt === 'none' ? 'Никуда' : opt === 'session' ? 'На сеанс' : 'Внешний URL'}
                </button>
              ))}
            </div>
            {linkType === 'session' && (
              <select
                className="input"
                value={linkSessionId}
                onChange={(e) => setLinkSessionId(e.target.value)}
              >
                <option value="" disabled>Выберите сеанс…</option>
                {(sessions as any[])?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title} · {s.cinema_name} · {formatDateTime(s.starts_at)}
                  </option>
                ))}
              </select>
            )}
            {linkType === 'url' && (
              <input
                className="input"
                placeholder="https://…"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            )}
          </div>

          <button
            className="btn-primary"
            disabled={
              !imageFileId ||
              addMut.isPending ||
              uploading ||
              (linkType === 'session' && !linkSessionId) ||
              (linkType === 'url' && !linkUrl)
            }
            onClick={() => addMut.mutate()}
          >
            Добавить
          </button>
        </div>
      </div>
    </div>
  );
}
